import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { useTheme, css } from 'styled-components';
import { cardBackground, cardBorder, cardShadow } from '../styles/themeHelpers';
import { getPublicFieldById, getPublicFieldMaps, getPublicFieldOperations, getMyFieldMaps, getOwnedOperations, FieldMap, FieldOwnedOperation } from '../services/fieldService';
import fieldNewsService from '../services/fieldNewsService';
import { Field } from '../types/field';
import { FieldNewsDto, CreateFieldNewsRequest, UpdateFieldNewsRequest } from '../services/fieldNewsService';
import NewsDetailModal from '../components/field/NewsDetailModal';
import CreateFieldNewsModal, { CreateNewsData } from '../components/field/CreateFieldNewsModal';
import ModernBackground from '../components/common/ModernBackground';
import { useFieldAuth } from '../contexts/FieldContext';
import { useRefereeAuth } from '../contexts/RefereeContext';
import GamesSection from '../components/field/GamesSection';
import LiveGamesSection from '../components/field/LiveGamesSection';
import UpcomingEventsSection from '../components/field/UpcomingEventsSection';
import ReviewSummary from '../components/common/ReviewSummary';
import ReviewModal from '../components/common/ReviewModal';
import ReviewsList from '../components/common/ReviewsList';
import { useAuth } from '../contexts/AuthContext';
import reviewService from '../services/reviewService';
import { ReviewSummary as ReviewSummaryType, Review } from '../types/review';
import RefereeList from '../components/referee/RefereeList';
import InviteRefereeModal, { InviteRefereeData } from '../components/referee/InviteRefereeModal';
import refereeService, { RefereeDto } from '../services/refereeService';
import { WeeklyCalendar } from '../components/schedule';
import FieldMapsSection from '../components/field/FieldMapsSection';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/common/NotificationContainer';
import ConfirmModal from '../components/common/ConfirmModal';
import { useConfirmModal } from '../hooks/useConfirmModal';
import { getFutureEvents } from '../services/scheduleService';
import { FieldScheduleEntry } from '../types/schedule';
import { updateFieldInfo } from '../services/fieldService';
import FieldInfoButton from '../components/field/FieldInfoButton';
import { mapsAndNodesInfo } from '../components/field/fieldMapsInfo';
import FieldSubscriptionSection from '../components/field/FieldSubscriptionSection';
import * as waiverService from '../services/waiverService';
import { WaiverStatusResponse, WaiverTemplate } from '../types/waiver';

// Helpers to pull consistent Shadow Grid styling from the theme profile
const shadowCardBackground = (theme: any) => cardBackground(theme);
const shadowCardBorder = (theme: any) => cardBorder(theme);
const shadowCardShadow = (theme: any) => cardShadow(theme);
const shadowHeaderBackground = (theme: any) => theme.styles?.headerBackground ?? shadowCardBackground(theme);
const shadowHeaderBorder = (theme: any) => theme.styles?.headerBorder ?? shadowCardBorder(theme);
const shadowTabBackground = (theme: any) => 'rgba(5, 6, 8, 0.6)';
const shadowTabBorder = (theme: any) => theme.colors?.borderLight ?? shadowCardBorder(theme);
const shadowInfoBorder = (theme: any) => theme.colors?.borderMuted ?? 'rgba(0, 255, 170, 0.12)';
const shadowNewsBackground = (theme: any) => shadowCardBackground(theme);
const shadowNewsBorder = (theme: any) => shadowCardBorder(theme);
const shadowNewsMetaBorder = (theme: any) => theme.colors?.borderMuted ?? 'rgba(0, 255, 170, 0.12)';
const shadowEmptyStateColor = (theme: any) => theme.colors?.textMuted ?? '#666';
const shadowSpinnerBorder = (theme: any) => theme.colors?.borderLight ?? 'rgba(0, 255, 170, 0.3)';
const shadowSpinnerTopBorder = (theme: any) => theme.colors?.primary ?? '#00d4ff';

const shadowGridChrome = css`
  ${({ theme }) => theme.name === 'shadowGrid' && css`
    position: relative;
    border: 1px solid rgba(0, 212, 255, 0.35);
    background: ${cardBackground(theme)};
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.6), 0 0 28px rgba(0, 212, 255, 0.2);
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 1px solid rgba(0, 212, 255, 0.2);
      opacity: 0.25;
      pointer-events: none;
    }
  `}
`;

interface FieldThemeProps {
  $night?: boolean;
}

const Background = styled.div<FieldThemeProps>`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients.background || theme.colors.background};
  position: relative;
  overflow: hidden;
`;


const Container = styled.div<FieldThemeProps>`
  max-width: 1200px;
  margin: 0 auto;
  padding: 100px 20px 20px 20px;
  position: relative;
  z-index: 2;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const Header = styled.div<FieldThemeProps>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  padding: ${({ $night }) => ($night ? '30px' : '30px')};
  background: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return theme.colors.surface;
    return $night ? theme.colors.surface : shadowHeaderBackground(theme);
  }};
  border: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return `1px solid ${theme.colors.borderLight}`;
    return $night ? 'none' : `1px solid ${shadowHeaderBorder(theme)}`;
  }};
  border-radius: ${({ $night }) => ($night ? '0' : '18px')};
  ${({ $night, theme }) => (!$night && theme.name === 'shadowGrid') && shadowGridChrome};
`;

const FieldTitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FieldTitle = styled.h1<FieldThemeProps>`
  color: ${({ $night, theme }) => ($night ? theme.colors.primary : '#00d4ff')};
  margin: 0;
  font-size: ${({ $night }) => ($night ? '2.5rem' : '2.5rem')};
  font-weight: bold;
  text-shadow: 0 0 20px ${({ $night, theme }) => ($night ? 'rgba(0, 212, 255, 0.35)' : 'rgba(0, 212, 255, 0.5)')};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Rajdhani', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  text-transform: ${({ $night }) => ($night ? 'uppercase' : 'none')};
  letter-spacing: ${({ $night }) => ($night ? '2px' : 'normal')};
`;

const FieldType = styled.div<FieldThemeProps>`
  color: ${({ $night, theme }) => ($night ? theme.colors.textSecondary : '#888')};
  font-size: 0.9rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const Tabs = styled.div<FieldThemeProps>`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ $night }) => ($night ? '10px' : '10px')};
  margin-bottom: 30px;
  background: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return 'transparent';
    return $night ? 'transparent' : shadowTabBackground(theme);
  }};
  border-radius: ${({ $night }) => ($night ? '0' : '10px')};
  padding: ${({ $night }) => ($night ? '0' : '5px')};
  border: ${({ $night, theme }) => ($night ? 'none' : `1px solid ${shadowTabBorder(theme)}`)};
  box-shadow: ${({ $night }) => ($night ? 'none' : 'none')};
`;

const Tab = styled.div<FieldThemeProps & { $active: boolean }>`
  padding: 15px 20px;
  text-align: center;
  cursor: pointer;
  border-radius: ${({ $night }) => ($night ? '12px' : '8px')};
  transition: all 0.3s ease;
  background: ${props => (props.$active ? props.theme.colors.primary : 'transparent')};
  color: ${props => (props.$active ? (props.theme.name === 'modernWarfare' ? '#00121f' : (props.$night ? '#00121f' : 'white')) : props.theme.colors.textSecondary)};
  font-weight: ${props => (props.$active ? 'bold' : 'normal')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $night }) => ($night ? '10px' : '0')};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  text-transform: ${({ $night }) => ($night ? 'uppercase' : 'none')};
  letter-spacing: ${({ $night }) => ($night ? '1px' : 'normal')};
  white-space: nowrap;

  &:hover {
    background: ${props => (props.$active ? props.theme.colors.primaryDark : props.theme.colors.surfaceHover)};
    color: ${props => (props.$active ? (props.theme.name === 'modernWarfare' ? '#00121f' : (props.$night ? '#00121f' : 'white')) : props.theme.colors.primary)};
  }
`;

const TabText = styled.span`
  white-space: nowrap;
`;

const TabCount = styled.span<FieldThemeProps>`
  background: ${({ $night, theme }) => ($night ? theme.colors.surfaceHover : 'rgba(255, 255, 255, 0.2)')};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  margin-left: 5px;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const TabContent = styled.div<FieldThemeProps>`
  background: transparent;
  border-radius: 0;
  border: none;
  box-shadow: none;
`;

const OverviewGrid = styled.div<FieldThemeProps>`
  display: grid;
  grid-template-columns: ${({ $night }) => ($night ? 'repeat(auto-fit, minmax(320px, 1fr))' : 'repeat(auto-fit, minmax(300px, 1fr))')};
  gap: ${({ $night }) => ($night ? '24px' : '20px')};
  margin-bottom: ${({ $night }) => ($night ? '32px' : '30px')};
`;

const SectionStack = styled.div<FieldThemeProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ $night }) => ($night ? '32px' : '20px')};
`;

const SectionCard = styled.div<FieldThemeProps>`
  background: rgba(5, 7, 10, 0.85);
  border: 1px solid ${({ theme }) => theme.colors?.borderLight || 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  margin-bottom: 25px;
`;

const SectionHeader = styled.div<FieldThemeProps>`
  background: rgba(255, 255, 255, 0.03);
  padding: 18px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const SectionTitle = styled.h3<FieldThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : '#00d4ff')};
  margin: 0;
  font-size: 1.1rem;
  font-weight: bold;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Rajdhani', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  letter-spacing: ${({ $night }) => ($night ? '1px' : 'normal')};
`;

const SectionContent = styled.div<FieldThemeProps>`
  padding: 24px;
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 12px;
`;

const InfoRow = styled.div<FieldThemeProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderMuted : shadowInfoBorder(theme))};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span<FieldThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.textSecondary : '#b0b0b0')};
  font-weight: 500;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  text-transform: ${({ $night }) => ($night ? 'uppercase' : 'none')};
  letter-spacing: ${({ $night }) => ($night ? '0.5px' : 'normal')};
`;

const InfoValue = styled.span<FieldThemeProps>`
  color: ${({ $night, theme }) => ($night ? theme.colors.text : '#fff')};
  font-weight: bold;
  text-align: right;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const InfoLink = styled.a<FieldThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : '#00d4ff')};
  text-decoration: none;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};

  &:hover {
    text-decoration: underline;
  }
`;

// Edit tab styled components
const EditInput = styled.input<FieldThemeProps>`
  width: 100%;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : 'rgba(0, 212, 255, 0.3)')};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 10px ${({ theme }) => theme.colors.primary}40;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const EditTextarea = styled.textarea<FieldThemeProps>`
  width: 100%;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : 'rgba(0, 212, 255, 0.3)')};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 10px ${({ theme }) => theme.colors.primary}40;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const EditFieldGroup = styled.div`
  margin-bottom: 20px;
`;

const EditFieldLabel = styled.label<FieldThemeProps>`
  display: block;
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.textSecondary : '#b0b0b0')};
  font-weight: 500;
  font-size: 0.9rem;
  margin-bottom: 8px;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const GameTypeCheckbox = styled.label<FieldThemeProps & { $checked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: ${({ $checked }) => $checked ? 'rgba(0, 212, 255, 0.15)' : 'rgba(0, 0, 0, 0.3)'};
  border: 1px solid ${({ $checked, theme }) => $checked ? theme.colors.primary : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${({ $checked, theme }) => $checked ? theme.colors.primary : theme.colors.textSecondary};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: rgba(0, 212, 255, 0.1);
  }

  input {
    display: none;
  }
`;

const GameTypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
`;

const EditButtonsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const SecondaryButton = styled.button<FieldThemeProps>`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const SectionParagraph = styled.p<FieldThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.textSecondary : '#ccc')};
  line-height: 1.6;
  margin: 0;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const RankInfo = styled.div<FieldThemeProps>`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const RankBadge = styled.div<FieldThemeProps & { color?: string }>`
  background: ${({ $night, theme }) => $night ? theme.gradients.primary : 'linear-gradient(135deg, #4caf50, #3f8d45)'};
  color: ${({ $night }) => $night ? '#00121f' : '#02150a'};
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  letter-spacing: 1px;
`;

const RankLevel = styled.div<FieldThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : '#00d4ff')};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  font-size: 1rem;
  letter-spacing: 1px;
`;

const StatusBadge = styled.div<FieldThemeProps & { status: 'active' | 'inactive' | 'approved' | 'pending' | 'verified' }>`
  padding: 6px 12px;
  border-radius: ${({ $night }) => ($night ? '999px' : '20px')};
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  
  ${props => {
    if (props.$night || props.theme.name === 'modernWarfare') {
      switch (props.status) {
        case 'active':
        case 'approved':
          return `
            background: ${props.theme.gradients.primary};
            color: #00121f;
          `;
        case 'verified':
          return `
            background: linear-gradient(135deg, #00ff88, #00cc66);
            color: white;
          `;
        case 'pending':
          return `
            background: linear-gradient(135deg, #ffaa00, #ff8800);
            color: white;
          `;
        case 'inactive':
        default:
          return `
            background: linear-gradient(135deg, #666, #444);
            color: #ccc;
          `;
      }
    } else {
      switch (props.status) {
        case 'active':
        case 'approved':
          return `
            background: linear-gradient(135deg, #00d4ff, #0099cc);
            color: white;
          `;
        case 'verified':
          return `
            background: linear-gradient(135deg, #00ff88, #00cc66);
            color: white;
          `;
        case 'pending':
          return `
            background: linear-gradient(135deg, #ffaa00, #ff8800);
            color: white;
          `;
        case 'inactive':
        default:
          return `
            background: linear-gradient(135deg, #666, #444);
            color: #ccc;
          `;
      }
    }
  }}
`;

const NewsItem = styled.div<FieldThemeProps & { isPriority?: boolean }>`
  background: ${props => (props.$night || props.theme.name === 'modernWarfare')
    ? (props.isPriority ? 'linear-gradient(135deg, rgba(255, 170, 0, 0.1), rgba(255, 136, 0, 0.05))' : props.theme.colors.surface)
    : (props.isPriority ? 'linear-gradient(135deg, rgba(255, 170, 0, 0.1), rgba(255, 136, 0, 0.05))' : shadowNewsBackground(props.theme))
  };
  border: 1px solid ${props => (props.$night || props.theme.name === 'modernWarfare')
    ? (props.isPriority ? 'rgba(255, 170, 0, 0.3)' : props.theme.colors.borderLight)
    : (props.isPriority ? 'rgba(255, 170, 0, 0.3)' : shadowNewsBorder(props.theme))
  };
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: ${({ $night, theme }) => ($night ? theme.shadows.card : '0 5px 20px rgba(0, 0, 0, 0.3)')};

  &:hover {
    transform: translateY(-2px);
    border-color: ${props => props.isPriority ? '#ffaa00' : ((props.$night || props.theme.name === 'modernWarfare') ? props.theme.colors.primary : shadowCardBorder(props.theme))};
    box-shadow: ${({ $night, theme }) => ($night ? theme.shadows.card : '0 5px 20px rgba(0, 0, 0, 0.3)')};
  }

  ${props => props.isPriority && `
    &::before {
      content: 'PRIORITY';
      position: absolute;
      top: 15px;
      right: 20px;
      background: linear-gradient(135deg, #ffaa00, #ff8800);
      color: white;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 0.7rem;
      font-weight: bold;
      letter-spacing: 0.5px;
      font-family: 'Courier New', monospace;
    }
  `}
`;

const NewsTitle = styled.h4<FieldThemeProps & { $highlight?: boolean; $hasBadge?: boolean }>`
  color: ${({ $night, theme, $highlight }) => ($highlight ? '#ffaa00' : (($night || theme.name === 'modernWarfare') ? theme.colors.primary : '#00d4ff'))};
  margin: 0 0 10px 0;
  font-size: 1.2rem;
  font-weight: bold;
  padding-right: ${({ $hasBadge }) => ($hasBadge ? '80px' : '0')};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Rajdhani', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  text-transform: ${({ $night }) => ($night ? 'uppercase' : 'none')};
  letter-spacing: ${({ $night }) => ($night ? '0.5px' : 'normal')};
`;

const NewsContent = styled.p<FieldThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.textSecondary : '#ccc')};
  line-height: 1.6;
  margin: 0 0 15px 0;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const NewsMeta = styled.div<FieldThemeProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.textMuted : '#888')};
  font-size: 0.85rem;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid ${({ $night, theme }) => ($night ? 'rgba(255,255,255,0.1)' : shadowNewsMetaBorder(theme))};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 5px;
    align-items: flex-start;
  }
`;


const EmptyState = styled.div<FieldThemeProps>`
  text-align: center;
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.textSecondary : shadowEmptyStateColor(theme))};
  font-style: italic;
  padding: 40px 20px;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const LoadingSpinner = styled.div<FieldThemeProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;

  &::after {
    content: '';
    width: 50px;
    height: 50px;
    border: 5px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : shadowSpinnerBorder(theme))};
    border-top: 5px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : shadowSpinnerTopBorder(theme))};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Maps Grid Styles
const MapsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const MapCard = styled.div<FieldThemeProps>`
  background: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.surface : 'rgba(0, 20, 40, 0.6)')};
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : 'rgba(0, 212, 255, 0.2)')};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  }
`;

const MapImage = styled.div<FieldThemeProps & { $imageUrl?: string }>`
  width: 100%;
  height: 160px;
  background: ${({ $imageUrl, $night, theme }) =>
    $imageUrl
      ? `url(${$imageUrl}) center/cover no-repeat`
      : ($night || theme.name === 'modernWarfare' ? theme.colors.surfaceHover : 'rgba(0, 30, 60, 0.8)')
  };
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.9rem;
`;

const MapContent = styled.div`
  padding: 16px;
`;

const MapName = styled.h4<FieldThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : '#00d4ff')};
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const MapDescription = styled.p<FieldThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.textSecondary : '#b0b0b0')};
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const ActionButton = styled.button<FieldThemeProps>`
  background: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : '#00d4ff')};
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : '#00d4ff')};
  color: #00121f;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primaryDark : '#00e0ff')};
    box-shadow: 0 0 15px ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? 'rgba(0, 255, 136, 0.45)' : 'rgba(0, 212, 255, 0.45)')};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const UnifiedFieldPage: React.FC = () => {
  const { fieldId } = useParams<{ fieldId: string }>();
  const navigate = useNavigate();
  const { field: authField, isFieldAuthenticated, refreshFieldData } = useFieldAuth();
  const { referee } = useRefereeAuth();
  const { user } = useAuth();
  const theme = useTheme();
  const isNightOps = theme.name === 'nightOps';
  const isModernWarfare = theme.name === 'modernWarfare';

  // Owner mode detection - owner is viewing their own field
  const isOwner = isFieldAuthenticated && !!authField?.id && authField.id === fieldId;

  // Notifications and confirm modal
  const { notifications, showSuccess, showError, removeNotification } = useNotification();
  const { confirm, modalProps: confirmModalProps } = useConfirmModal();

  // Helper to add notification
  const addNotification = (message: string, type: 'success' | 'error') => {
    if (type === 'success') {
      showSuccess('Success', message);
    } else {
      showError('Error', message);
    }
  };

  const [field, setField] = useState<Field | null>(null);
  const [fieldNews, setFieldNews] = useState<FieldNewsDto[]>([]);
  const [fieldMaps, setFieldMaps] = useState<FieldMap[]>([]);
  const [fieldOperations, setFieldOperations] = useState<FieldOwnedOperation[]>([]);
  const [loading, setLoading] = useState(true);
  const [newsLoading, setNewsLoading] = useState(true);
  const [mapsLoading, setMapsLoading] = useState(true);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [operationsLoaded, setOperationsLoaded] = useState(false);

  // Owner-specific states
  const [isCreateNewsModalOpen, setIsCreateNewsModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<FieldNewsDto | null>(null);
  const [futureEvents, setFutureEvents] = useState<FieldScheduleEntry[]>([]);
  const [isInviteRefereeModalOpen, setIsInviteRefereeModalOpen] = useState(false);
  const [referees, setReferees] = useState<RefereeDto[]>([]);
  const [refereesLoading, setRefereesLoading] = useState(false);

  // Edit tab state
  const [editingField, setEditingField] = useState({
    name: '',
    address: '',
    description: '',
    phone: '',
    website: '',
    supportedGameTypes: [] as string[],
  });
  const [isSaving, setIsSaving] = useState(false);

  // URL-based tabs - edit tab only for owner
  type TabType = 'overview' | 'live' | 'missions' | 'maps' | 'news' | 'operations' | 'reviews' | 'referees' | 'schedule' | 'subscription' | 'edit';
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabType) || 'overview';

  const setActiveTab = useCallback((tab: TabType) => {
    setSearchParams({ tab }, { replace: false });
  }, [setSearchParams]);

  const [selectedNews, setSelectedNews] = useState<FieldNewsDto | null>(null);
  const [isNewsDetailModalOpen, setIsNewsDetailModalOpen] = useState(false);

  // Review states
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewSummary, setReviewSummary] = useState<ReviewSummaryType | null>(null);
  const [myReview, setMyReview] = useState<Review | null>(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Waiver states
  const [waiverStatus, setWaiverStatus] = useState<WaiverStatusResponse | null>(null);
  const [nextYearTemplate, setNextYearTemplate] = useState<WaiverTemplate | null>(null);
  const [waiverLoading, setWaiverLoading] = useState(false);

  // Initialize editing field when field data loads
  useEffect(() => {
    if (field) {
      setEditingField({
        name: field.name || '',
        address: field.address || '',
        description: field.description || '',
        phone: field.phone || '',
        website: field.website || '',
        supportedGameTypes: field.supportedGameTypes || [],
      });
    }
  }, [field]);

  useEffect(() => {
    if (fieldId) {
      loadFieldData();
      loadFieldNews();
      loadFieldMaps();
      if (isOwner) {
        loadFutureEvents();
        loadReferees();
      }
    }
  }, [fieldId, isOwner]);

  // Загрузка статуса вейвера
  const loadWaiverStatus = async () => {
    if (!fieldId) return;

    setWaiverLoading(true);
    try {
      // Для залогиненного пользователя проверяем статус
      if (user) {
        const status = await waiverService.checkWaiverStatus(fieldId);
        setWaiverStatus(status);
      }

      // Проверяем, есть ли шаблон на следующий год (если до нового года меньше 30 дней)
      const now = new Date();
      const endOfYear = new Date(now.getFullYear(), 11, 31);
      const daysUntilNewYear = Math.ceil((endOfYear.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysUntilNewYear <= 30) {
        try {
          const nextYear = now.getFullYear() + 1;
          const template = await waiverService.getTemplateByYear(fieldId, nextYear);
          setNextYearTemplate(template);
        } catch {
          // Нет шаблона на следующий год - это нормально
          setNextYearTemplate(null);
        }
      }
    } catch (err) {
      console.error('Error loading waiver status:', err);
    } finally {
      setWaiverLoading(false);
    }
  };

  // Загрузка отзывов
  const loadReviews = async () => {
    if (!fieldId) return;
    
    setReviewsLoading(true);
    try {
      const [summaryData, myReviewData] = await Promise.all([
        reviewService.getFieldReviewSummary(fieldId),
        user ? reviewService.getMyReview('field', fieldId) : Promise.resolve(null)
      ]);

      setReviewSummary(summaryData);
      setMyReview(myReviewData);
    } catch (err) {
      console.error('Error loading reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    if (fieldId) {
      loadReviews();
    }
  }, [fieldId, user]);

  useEffect(() => {
    if (fieldId) {
      loadWaiverStatus();
    }
  }, [fieldId, user]);

  // Обработчик отправки отзыва
  const handleReviewSubmitted = () => {
    loadReviews(); // Перезагружаем отзывы
    setIsReviewModalOpen(false);
  };

  // No redirect for owner - unified page handles both modes

  const loadFieldData = async () => {
    try {
      setLoading(true);
      const fieldData = await getPublicFieldById(fieldId!);
      setField(fieldData);
    } catch (error) {
      console.error('Failed to load field data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFieldNews = async () => {
    try {
      setNewsLoading(true);
      // Owner sees all news (including unpublished), public sees only published
      const news = isOwner
        ? await fieldNewsService.getAllFieldNews(fieldId!)
        : await fieldNewsService.getPublicFieldNews(fieldId!);
      setFieldNews(news);
    } catch (error) {
      console.error('Failed to load field news:', error);
      setFieldNews([]);
    } finally {
      setNewsLoading(false);
    }
  };

  const loadFieldMaps = async () => {
    try {
      setMapsLoading(true);
      // Owner gets maps with full device/node data
      const maps = isOwner
        ? await getMyFieldMaps()
        : await getPublicFieldMaps(fieldId!);
      setFieldMaps(maps);
    } catch (error) {
      console.error('Failed to load field maps:', error);
      setFieldMaps([]);
    } finally {
      setMapsLoading(false);
    }
  };

  const loadFieldOperations = async () => {
    if (!fieldId || operationsLoaded) return;

    try {
      setOperationsLoading(true);
      // Owner uses authenticated API
      const operations = isOwner
        ? await getOwnedOperations()
        : await getPublicFieldOperations(fieldId);
      setFieldOperations(operations);
      setOperationsLoaded(true);
    } catch (error) {
      console.error('Failed to load field operations:', error);
      setFieldOperations([]);
    } finally {
      setOperationsLoading(false);
    }
  };

  // Lazy load operations when tab is clicked
  useEffect(() => {
    if (activeTab === 'operations' && !operationsLoaded && fieldId) {
      loadFieldOperations();
    }
  }, [activeTab, operationsLoaded, fieldId]);

  // Owner-specific data loaders
  const loadFutureEvents = async () => {
    if (!fieldId) return;
    try {
      const events = await getFutureEvents(fieldId);
      setFutureEvents(events.filter(e => e.entryType === 'event'));
    } catch (error) {
      console.error('Failed to load future events:', error);
    }
  };

  const loadReferees = async () => {
    if (!fieldId) return;
    try {
      setRefereesLoading(true);
      const refs = await refereeService.getFieldReferees();
      setReferees(refs);
    } catch (error) {
      console.error('Failed to load referees:', error);
    } finally {
      setRefereesLoading(false);
    }
  };

  // Owner News CRUD handlers
  const handleCreateNews = async (newsData: CreateNewsData) => {
    try {
      const request: CreateFieldNewsRequest = {
        title: newsData.title,
        content: newsData.content,
        authorName: authField?.name || 'Field Admin',
        isPublished: true,
        isPriority: newsData.isPriority,
        imageUrl: undefined,
        imageUrls: newsData.imageUrls,
        priority: newsData.isPriority ? 1 : 0,
        linkedEventId: newsData.linkedEventId,
      };
      await fieldNewsService.createFieldNews(request, newsData.imageFile);
      addNotification('News created successfully', 'success');
      setIsCreateNewsModalOpen(false);
      loadFieldNews();
    } catch (error: any) {
      addNotification(error.message || 'Failed to create news', 'error');
    }
  };

  const handleUpdateNews = async (newsData: CreateNewsData) => {
    if (!editingNews) return;
    try {
      const request: UpdateFieldNewsRequest = {
        title: newsData.title,
        content: newsData.content,
        authorName: editingNews.authorName,
        isPublished: editingNews.isPublished,
        isPriority: newsData.isPriority,
        imageUrl: undefined,
        imageUrls: newsData.imageUrls,
        priority: newsData.isPriority ? 1 : 0,
        linkedEventId: newsData.linkedEventId,
      };
      await fieldNewsService.updateFieldNews(editingNews.id, request, newsData.imageFile);
      addNotification('News updated successfully', 'success');
      setEditingNews(null);
      setIsCreateNewsModalOpen(false);
      loadFieldNews();
    } catch (error: any) {
      addNotification(error.message || 'Failed to update news', 'error');
    }
  };

  const handleDeleteNews = async (newsId: string) => {
    const confirmed = await confirm({
      title: 'Delete News',
      message: 'Are you sure you want to delete this news article? This action cannot be undone.',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await fieldNewsService.deleteFieldNews(newsId);
      addNotification('News deleted successfully', 'success');
      loadFieldNews();
    } catch (error: any) {
      addNotification(error.message || 'Failed to delete news', 'error');
    }
  };

  const handleEditNews = (news: FieldNewsDto) => {
    setEditingNews(news);
    setIsCreateNewsModalOpen(true);
  };

  const handleToggleNewsPublish = async (news: FieldNewsDto) => {
    try {
      const request: UpdateFieldNewsRequest = {
        title: news.title,
        content: news.content,
        authorName: news.authorName,
        isPublished: !news.isPublished,
        isPriority: news.isPriority,
        imageUrl: news.imageUrl,
        imageUrls: news.imageUrls,
        priority: news.priority,
        linkedEventId: news.linkedEventId,
      };
      await fieldNewsService.updateFieldNews(news.id, request);
      addNotification(news.isPublished ? 'News unpublished' : 'News published', 'success');
      loadFieldNews();
    } catch (error: any) {
      addNotification(error.message || 'Failed to toggle publish status', 'error');
    }
  };

  // Owner Referee handlers
  const handleInviteReferee = async (data: InviteRefereeData) => {
    try {
      await refereeService.inviteReferee({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        gameTypes: data.gameTypes,
        isMainReferee: data.isMainReferee,
      });
      addNotification('Referee invitation sent successfully', 'success');
      setIsInviteRefereeModalOpen(false);
      loadReferees();
    } catch (error: any) {
      addNotification(error.message || 'Failed to invite referee', 'error');
    }
  };

  // Edit tab handlers
  const handleResetEditForm = () => {
    if (field) {
      setEditingField({
        name: field.name || '',
        address: field.address || '',
        description: field.description || '',
        phone: field.phone || '',
        website: field.website || '',
        supportedGameTypes: field.supportedGameTypes || [],
      });
    }
  };

  const handleSaveFieldInfo = async () => {
    setIsSaving(true);
    try {
      await updateFieldInfo({
        name: field?.name || '',
        address: editingField.address,
        description: editingField.description,
        phone: editingField.phone,
        website: editingField.website,
        supportedGameTypes: editingField.supportedGameTypes,
        state: field?.state || 'Florida',
      });
      addNotification('Field information updated successfully', 'success');
      loadFieldData();
      refreshFieldData();
    } catch (error: any) {
      addNotification(error.message || 'Failed to update field information', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleGameType = (gameType: string) => {
    setEditingField(prev => {
      const newTypes = prev.supportedGameTypes.includes(gameType)
        ? prev.supportedGameTypes.filter(t => t !== gameType)
        : [...prev.supportedGameTypes, gameType];
      return { ...prev, supportedGameTypes: newTypes };
    });
  };

  // Active referees for calendar
  const activeReferees = referees
    .filter(r => Object.values(r.fieldAssignments).includes('active'))
    .map(r => ({ id: r.id, name: `${r.firstName} ${r.lastName}` }));

  // Referee management handlers (for owner mode)
  const handleUpdateRefereeStatus = async (refereeId: string, status: string) => {
    try {
      await refereeService.updateRefereeStatus(refereeId, { status });
      addNotification(`Referee status updated to ${status}`, 'success');
      loadReferees();
    } catch (error: any) {
      addNotification(error.message || 'Failed to update referee status', 'error');
    }
  };

  const handleRemoveReferee = async (refereeId: string) => {
    try {
      await refereeService.removeRefereeFromField(refereeId);
      addNotification('Referee removed from field', 'success');
      loadReferees();
    } catch (error: any) {
      addNotification(error.message || 'Failed to remove referee', 'error');
    }
  };

  const handleSetMainReferee = async (refereeId: string, isMain: boolean) => {
    try {
      await refereeService.setMainReferee(refereeId, isMain);
      addNotification(isMain ? 'Referee set as main' : 'Main referee status removed', 'success');
      loadReferees();
    } catch (error: any) {
      addNotification(error.message || 'Failed to update main referee', 'error');
    }
  };

  const handleOpenNewsDetail = (news: FieldNewsDto) => {
    setSelectedNews(news);
    setIsNewsDetailModalOpen(true);
  };

  const handleCloseNewsDetail = () => {
    setSelectedNews(null);
    setIsNewsDetailModalOpen(false);
  };

  if (loading) {
    return (
      <Background $night={isNightOps}>
        {isModernWarfare && <ModernBackground />}
        <Container $night={isNightOps}>
          <LoadingSpinner $night={isNightOps} />
        </Container>
      </Background>
    );
  }

  if (!field) {
    return (
      <Background $night={isNightOps}>
        {isModernWarfare && <ModernBackground />}
        <Container $night={isNightOps}>
          <EmptyState $night={isNightOps}>Field not found</EmptyState>
        </Container>
      </Background>
    );
  }

  return (
    <Background $night={isNightOps}>
      {isModernWarfare && <ModernBackground />}
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
      <ConfirmModal {...confirmModalProps} />
      <Container $night={isNightOps}>
        <Header $night={isNightOps}>
          <FieldTitleSection>
            <FieldTitle $night={isNightOps}>{field.name || 'Unknown Field'}</FieldTitle>
            <FieldType $night={isNightOps}>
              {isOwner ? 'YOUR FIELD' : 'FIELD'}
            </FieldType>
          </FieldTitleSection>
          {isOwner && (
            <div style={{ display: 'flex', gap: '12px' }}>
              <ActionButton $night={isNightOps} onClick={() => navigate('/field/management')}>
                Management
              </ActionButton>
              <ActionButton $night={isNightOps} onClick={() => navigate(`/fields/${fieldId}/analytics`)}>
                Analytics
              </ActionButton>
            </div>
          )}
        </Header>

        <Tabs $night={isNightOps}>
          <Tab
            $active={activeTab === 'overview'}
            $night={isNightOps}
            onClick={() => setActiveTab('overview')}
          >
            <TabText>Overview</TabText>
          </Tab>
          <Tab
            $active={activeTab === 'live'}
            $night={isNightOps}
            onClick={() => setActiveTab('live')}
          >
            <TabText>Live Games</TabText>
          </Tab>
          <Tab
            $active={activeTab === 'missions'}
            $night={isNightOps}
            onClick={() => setActiveTab('missions')}
          >
            <TabText>Missions</TabText>
            <TabCount $night={isNightOps}>({field?.totalGames || 0})</TabCount>
          </Tab>
          <Tab
            $active={activeTab === 'maps'}
            $night={isNightOps}
            onClick={() => setActiveTab('maps')}
          >
            <TabText>Maps</TabText>
            <TabCount $night={isNightOps}>({fieldMaps.length})</TabCount>
          </Tab>
          <Tab
            $active={activeTab === 'news'}
            $night={isNightOps}
            onClick={() => setActiveTab('news')}
          >
            <TabText>News</TabText>
            <TabCount $night={isNightOps}>({fieldNews.length})</TabCount>
          </Tab>
          <Tab
            $active={activeTab === 'operations'}
            $night={isNightOps}
            onClick={() => setActiveTab('operations')}
          >
            <TabText>Operations</TabText>
          </Tab>
          <Tab
            $active={activeTab === 'reviews'}
            $night={isNightOps}
            onClick={() => setActiveTab('reviews')}
          >
            <TabText>Reviews</TabText>
            <TabCount $night={isNightOps}>({reviewSummary?.reviewCount || 0})</TabCount>
          </Tab>
          <Tab
            $active={activeTab === 'referees'}
            $night={isNightOps}
            onClick={() => setActiveTab('referees')}
          >
            <TabText>Referees</TabText>
            <TabCount $night={isNightOps}>({field?.judgeCount || 0})</TabCount>
          </Tab>
          <Tab
            $active={activeTab === 'schedule'}
            $night={isNightOps}
            onClick={() => setActiveTab('schedule')}
          >
            <TabText>Schedule</TabText>
          </Tab>
          {isOwner && (
            <>
              <Tab
                $active={activeTab === 'subscription'}
                $night={isNightOps}
                onClick={() => setActiveTab('subscription')}
              >
                <TabText>Subscription</TabText>
              </Tab>
              <Tab
                $active={activeTab === 'edit'}
                $night={isNightOps}
                onClick={() => setActiveTab('edit')}
              >
                <TabText>Edit Info</TabText>
              </Tab>
            </>
          )}
        </Tabs>

        <TabContent $night={isNightOps}>
          {activeTab === 'overview' && (
            <SectionStack $night={isNightOps}>
              <OverviewGrid $night={isNightOps}>
                {/* Main Information */}
                <SectionCard $night={isNightOps}>
                  <SectionHeader $night={isNightOps}>
                    <SectionTitle $night={isNightOps}>Main Information</SectionTitle>
                  </SectionHeader>
                  <SectionContent $night={isNightOps}>
                    <InfoGrid>
                      <InfoRow $night={isNightOps}>
                        <InfoLabel $night={isNightOps}>Active Judges:</InfoLabel>
                        <InfoValue $night={isNightOps}>{field.judgeCount || 0}</InfoValue>
                      </InfoRow>
                      <InfoRow $night={isNightOps}>
                        <InfoLabel $night={isNightOps}>Total Operations:</InfoLabel>
                        <InfoValue $night={isNightOps}>{field.totalOperations || 0}</InfoValue>
                      </InfoRow>
                      <InfoRow $night={isNightOps}>
                        <InfoLabel $night={isNightOps}>Total Missions:</InfoLabel>
                        <InfoValue $night={isNightOps}>{field.totalGames || 0}</InfoValue>
                      </InfoRow>
                      <InfoRow $night={isNightOps}>
                        <InfoLabel $night={isNightOps}>Airsoft Missions:</InfoLabel>
                        <InfoValue $night={isNightOps}>{field.totalAirsoftGames || 0}</InfoValue>
                      </InfoRow>
                      <InfoRow $night={isNightOps}>
                        <InfoLabel $night={isNightOps}>Paintball Missions:</InfoLabel>
                        <InfoValue $night={isNightOps}>{field.totalPaintballGames || 0}</InfoValue>
                      </InfoRow>
                      <InfoRow $night={isNightOps}>
                        <InfoLabel $night={isNightOps}>Speedball Missions:</InfoLabel>
                        <InfoValue $night={isNightOps}>{field.totalSpeedballGames || 0}</InfoValue>
                      </InfoRow>
                    </InfoGrid>
                  </SectionContent>
                </SectionCard>

                {/* Status and Rating */}
                <SectionCard $night={isNightOps}>
                  <SectionHeader $night={isNightOps}>
                    <SectionTitle $night={isNightOps}>Status and Rating</SectionTitle>
                  </SectionHeader>
                  <SectionContent $night={isNightOps}>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                      <StatusBadge $night={isNightOps} status={field.isApproved ? 'approved' : 'inactive'}>
                        {field.isApproved ? 'Active' : 'Inactive'}
                      </StatusBadge>
                      <StatusBadge $night={isNightOps} status={field.rating && field.rating >= 4 ? 'verified' : field.rating && field.rating >= 2.5 ? 'pending' : 'inactive'}>
                        {field.rating ? `${field.rating.toFixed(1)}/5` : 'No rating'}
                      </StatusBadge>
                    </div>
                  </SectionContent>
                </SectionCard>
              </OverviewGrid>

              {/* Waiver Section - показываем если не owner */}
              {!isOwner && (
                <SectionCard $night={isNightOps}>
                  <SectionHeader $night={isNightOps}>
                    <SectionTitle $night={isNightOps}>Liability Waiver</SectionTitle>
                  </SectionHeader>
                  <SectionContent $night={isNightOps}>
                    {waiverLoading ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                        Checking waiver status...
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {user ? (
                          <>
                            {/* Залогиненный пользователь */}
                            {waiverStatus?.hasValidWaiver ? (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '16px 20px',
                                background: 'rgba(0, 255, 136, 0.1)',
                                border: '1px solid rgba(0, 255, 136, 0.3)',
                                borderRadius: '10px',
                              }}>
                                <div>
                                  <div style={{ color: '#00ff88', fontWeight: 600, marginBottom: '4px' }}>
                                    ✓ Waiver Signed for {waiverStatus.year}
                                  </div>
                                  <div style={{ color: '#888', fontSize: '13px' }}>
                                    Signed on {new Date(waiverStatus.signedAt!).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '16px 20px',
                                background: 'rgba(255, 170, 0, 0.1)',
                                border: '1px solid rgba(255, 170, 0, 0.3)',
                                borderRadius: '10px',
                                flexWrap: 'wrap',
                                gap: '12px',
                              }}>
                                <div>
                                  <div style={{ color: '#ffaa00', fontWeight: 600, marginBottom: '4px' }}>
                                    Waiver Required
                                  </div>
                                  <div style={{ color: '#888', fontSize: '13px' }}>
                                    You must sign a liability waiver before playing at this field
                                  </div>
                                </div>
                                <ActionButton
                                  $night={isNightOps}
                                  onClick={() => navigate(`/waiver/${fieldId}`)}
                                >
                                  Sign Waiver
                                </ActionButton>
                              </div>
                            )}

                            {/* Следующий год - если до нового года меньше 30 дней и есть шаблон */}
                            {nextYearTemplate && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '16px 20px',
                                background: 'rgba(0, 212, 255, 0.1)',
                                border: '1px solid rgba(0, 212, 255, 0.3)',
                                borderRadius: '10px',
                                flexWrap: 'wrap',
                                gap: '12px',
                              }}>
                                <div>
                                  <div style={{ color: '#00d4ff', fontWeight: 600, marginBottom: '4px' }}>
                                    {nextYearTemplate.year} Waiver Available
                                  </div>
                                  <div style={{ color: '#888', fontSize: '13px' }}>
                                    Sign the waiver for next year now to save time later
                                  </div>
                                </div>
                                <ActionButton
                                  $night={isNightOps}
                                  onClick={() => navigate(`/waiver/${fieldId}?year=${nextYearTemplate.year}`)}
                                >
                                  Sign {nextYearTemplate.year} Waiver
                                </ActionButton>
                              </div>
                            )}
                          </>
                        ) : (
                          /* Гость без аккаунта */
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '16px 20px',
                            background: 'rgba(255, 170, 0, 0.1)',
                            border: '1px solid rgba(255, 170, 0, 0.3)',
                            borderRadius: '10px',
                            flexWrap: 'wrap',
                            gap: '12px',
                          }}>
                            <div>
                              <div style={{ color: '#ffaa00', fontWeight: 600, marginBottom: '4px' }}>
                                Waiver Required
                              </div>
                              <div style={{ color: '#888', fontSize: '13px' }}>
                                All players must sign a liability waiver before playing
                              </div>
                            </div>
                            <ActionButton
                              $night={isNightOps}
                              onClick={() => navigate(`/waiver/${fieldId}?mode=guest`)}
                            >
                              Sign Waiver
                            </ActionButton>
                          </div>
                        )}
                      </div>
                    )}
                  </SectionContent>
                </SectionCard>
              )}

              {/* Location Information */}
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>Location Information</SectionTitle>
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  <InfoGrid>
                    <InfoRow $night={isNightOps}>
                      <InfoLabel $night={isNightOps}>Address:</InfoLabel>
                      <InfoValue $night={isNightOps}>
                        {field.address ? (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(field.address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: isNightOps ? '#00ff88' : '#00d4ff', textDecoration: 'none' }}
                            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                          >
                            {field.address}
                          </a>
                        ) : 'Not specified'}
                      </InfoValue>
                    </InfoRow>
                    <InfoRow $night={isNightOps}>
                      <InfoLabel $night={isNightOps}>Phone:</InfoLabel>
                      <InfoValue $night={isNightOps}>{field.phone || 'Not specified'}</InfoValue>
                    </InfoRow>
                    <InfoRow $night={isNightOps}>
                      <InfoLabel $night={isNightOps}>Email:</InfoLabel>
                      <InfoValue $night={isNightOps}>{field.email}</InfoValue>
                    </InfoRow>
                  </InfoGrid>
                </SectionContent>
              </SectionCard>

              {/* Description */}
              {field.description && (
                <SectionCard $night={isNightOps}>
                  <SectionHeader $night={isNightOps}>
                    <SectionTitle $night={isNightOps}>Description</SectionTitle>
                  </SectionHeader>
                  <SectionContent $night={isNightOps}>
                    <SectionParagraph $night={isNightOps}>
                      {field.description}
                    </SectionParagraph>
                  </SectionContent>
                </SectionCard>
              )}

              {/* Upcoming Events - component handles its own wrapper */}
              {fieldId && (
                <UpcomingEventsSection
                  fieldId={fieldId}
                  onViewSchedule={() => setActiveTab('schedule')}
                  onNewsClick={handleOpenNewsDetail}
                  $night={isNightOps}
                />
              )}

              {/* Our Schedule */}
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>Our Schedule</SectionTitle>
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '20px',
                    padding: '20px 0'
                  }}>
                    <SectionParagraph $night={isNightOps} style={{ textAlign: 'center' }}>
                      Check our schedule for upcoming events, referee availability, and private bookings.
                    </SectionParagraph>
                    <ActionButton $night={isNightOps} onClick={() => setActiveTab('schedule')}>
                      View Schedule
                    </ActionButton>
                  </div>
                </SectionContent>
              </SectionCard>

              {/* Latest News - only show if there are published news */}
              {fieldNews.length > 0 && (
                <SectionCard $night={isNightOps}>
                  <SectionHeader $night={isNightOps}>
                    <SectionTitle $night={isNightOps}>Latest News</SectionTitle>
                  </SectionHeader>
                  <SectionContent $night={isNightOps}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      {fieldNews
                        .sort((a, b) => {
                          if (a.isPriority && !b.isPriority) return -1;
                          if (!a.isPriority && b.isPriority) return 1;
                          const dateA = new Date(a.publishedDate || a.createdDate);
                          const dateB = new Date(b.publishedDate || b.createdDate);
                          return dateB.getTime() - dateA.getTime();
                        })
                        .slice(0, 3)
                        .map(news => (
                          <NewsItem
                            key={news.id}
                            isPriority={news.isPriority}
                            $night={isNightOps}
                            onClick={() => handleOpenNewsDetail(news)}
                            style={{ cursor: 'pointer' }}
                          >
                            <NewsTitle $night={isNightOps} $highlight={news.isPriority} $hasBadge={news.isPriority}>
                              {news.title}
                            </NewsTitle>
                            <NewsContent $night={isNightOps}>
                              {news.content.length > 150 ? `${news.content.substring(0, 150)}...` : news.content}
                            </NewsContent>
                            <NewsMeta $night={isNightOps}>
                              <span>By: {news.authorName}</span>
                              <span>{new Date(news.publishedDate || news.createdDate).toLocaleDateString()}</span>
                            </NewsMeta>
                          </NewsItem>
                        ))}
                    </div>
                  </SectionContent>
                </SectionCard>
              )}

              {/* Recent Missions Section */}
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>Completed Missions</SectionTitle>
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  <GamesSection
                    fieldId={fieldId!}
                    fieldName={field?.name}
                    supportedGameTypes={field?.supportedGameTypes || ['airsoft']}
                    showFilters={true}
                    showPagination={true}
                    isPublic={true}
                  />
                </SectionContent>
              </SectionCard>

              {/* Field Reviews Summary */}
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>Field Reviews</SectionTitle>
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  <ReviewSummary
                    summary={reviewSummary || {
                      rating: 0,
                      ratingConfirmed: 0,
                      reviewCount: 0,
                      reviewCountConfirmed: 0,
                      totalReviews: 0,
                      ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
                      ratingDistributionConfirmed: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
                    }}
                    loading={reviewsLoading}
                    targetType="field"
                    targetId={fieldId || ''}
                    showWriteReview={true}
                    canWriteReview={!!user}
                    onWriteReview={() => setIsReviewModalOpen(true)}
                    writeReviewText={myReview ? 'Edit Review' : 'Write Review'}
                    writeReviewMessage={authField ? 'Fields cannot write reviews' : referee ? 'Referees cannot write reviews' : !user ? 'Login to write a review' : undefined}
                  />
                </SectionContent>
              </SectionCard>
            </SectionStack>
          )}

          {activeTab === 'live' && fieldId && (
            <SectionStack $night={isNightOps}>
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>Live Games</SectionTitle>
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  <LiveGamesSection
                    fieldId={fieldId}
                    refreshInterval={15000}
                  />
                </SectionContent>
              </SectionCard>
            </SectionStack>
          )}

          {activeTab === 'missions' && (
            <SectionStack $night={isNightOps}>
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>Field Missions</SectionTitle>
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  <GamesSection
                    fieldId={fieldId!}
                    fieldName={field?.name}
                    supportedGameTypes={field?.supportedGameTypes || ['airsoft']}
                    showFilters={true}
                    showPagination={true}
                    isPublic={true}
                  />
                </SectionContent>
              </SectionCard>
            </SectionStack>
          )}

          {activeTab === 'maps' && fieldId && (
            <SectionStack $night={isNightOps}>
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>Field Maps</SectionTitle>
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  <FieldMapsSection
                    fieldId={fieldId}
                    isOwner={isOwner}
                  />
                </SectionContent>
              </SectionCard>
            </SectionStack>
          )}

          {activeTab === 'news' && (
            <SectionStack $night={isNightOps}>
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>
                    {isOwner ? 'Manage News' : 'Latest News'}
                  </SectionTitle>
                  {isOwner && (
                    <ActionButton $night={isNightOps} onClick={() => {
                      setEditingNews(null);
                      setIsCreateNewsModalOpen(true);
                    }}>
                      + Create News
                    </ActionButton>
                  )}
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  {newsLoading ? (
                    <LoadingSpinner $night={isNightOps} />
                  ) : fieldNews.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {fieldNews
                        .sort((a, b) => {
                          if (a.isPriority !== b.isPriority) {
                            return b.isPriority ? 1 : -1;
                          }
                          return new Date(b.publishedDate || b.createdDate).getTime() -
                                 new Date(a.publishedDate || a.createdDate).getTime();
                        })
                        .map(news => (
                          <NewsItem
                            key={news.id}
                            isPriority={news.isPriority}
                            $night={isNightOps}
                            onClick={() => handleOpenNewsDetail(news)}
                            style={{ cursor: 'pointer', opacity: !news.isPublished ? 0.6 : 1 }}
                          >
                            {/* Owner: show unpublished badge */}
                            {isOwner && !news.isPublished && (
                              <span style={{
                                position: 'absolute',
                                top: '10px',
                                left: '15px',
                                background: '#666',
                                color: '#fff',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                              }}>
                                DRAFT
                              </span>
                            )}
                            <NewsTitle
                              $night={isNightOps}
                              $highlight={news.isPriority}
                              $hasBadge={news.isPriority || (isOwner && !news.isPublished)}
                            >
                              {news.title}
                            </NewsTitle>
                            <NewsContent $night={isNightOps}>
                              {news.content.length > 300 ? `${news.content.substring(0, 300)}...` : news.content}
                            </NewsContent>
                            <NewsMeta $night={isNightOps}>
                              <span>By: {news.authorName}</span>
                              <span>{new Date(news.publishedDate || news.createdDate).toLocaleDateString()}</span>
                            </NewsMeta>
                            {/* Owner actions */}
                            {isOwner && (
                              <div style={{
                                display: 'flex',
                                gap: '10px',
                                marginTop: '15px',
                                paddingTop: '10px',
                                borderTop: '1px solid rgba(255,255,255,0.1)'
                              }}
                              onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  onClick={() => handleEditNews(news)}
                                  style={{
                                    background: 'rgba(0, 212, 255, 0.2)',
                                    border: '1px solid rgba(0, 212, 255, 0.4)',
                                    color: '#00d4ff',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleToggleNewsPublish(news)}
                                  style={{
                                    background: news.isPublished ? 'rgba(255, 170, 0, 0.2)' : 'rgba(0, 255, 136, 0.2)',
                                    border: `1px solid ${news.isPublished ? 'rgba(255, 170, 0, 0.4)' : 'rgba(0, 255, 136, 0.4)'}`,
                                    color: news.isPublished ? '#ffaa00' : '#00ff88',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  {news.isPublished ? 'Unpublish' : 'Publish'}
                                </button>
                                <button
                                  onClick={() => handleDeleteNews(news.id)}
                                  style={{
                                    background: 'rgba(255, 68, 68, 0.2)',
                                    border: '1px solid rgba(255, 68, 68, 0.4)',
                                    color: '#ff4444',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </NewsItem>
                        ))}
                    </div>
                  ) : (
                    <EmptyState $night={isNightOps}>
                      {isOwner ? 'No news yet. Create your first news article!' : 'No news available'}
                    </EmptyState>
                  )}
                </SectionContent>
              </SectionCard>
            </SectionStack>
          )}

          {activeTab === 'operations' && fieldId && (
            <SectionStack $night={isNightOps}>
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>Field Operations</SectionTitle>
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  {operationsLoading ? (
                    <LoadingSpinner $night={isNightOps} />
                  ) : fieldOperations.length > 0 ? (
                    <div style={{ display: 'grid', gap: '16px' }}>
                      {fieldOperations.map((op) => (
                        <div
                          key={op.operationId}
                          onClick={() => navigate(`/operations/${op.operationId}`)}
                          style={{
                            background: isNightOps ? 'rgba(0, 212, 255, 0.05)' : 'rgba(0, 212, 255, 0.05)',
                            border: `1px solid ${isNightOps ? 'rgba(0, 212, 255, 0.2)' : 'rgba(0, 212, 255, 0.2)'}`,
                            borderRadius: '12px',
                            padding: '20px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)';
                            e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(0, 212, 255, 0.05)';
                            e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.2)';
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                            <h3 style={{ color: '#00d4ff', margin: '0 0 4px 0', fontSize: '18px', fontFamily: 'Orbitron, monospace' }}>
                              {op.operationName}
                            </h3>
                            {op.count > 1 && (
                              <span
                                style={{
                                  padding: '4px 12px',
                                  borderRadius: '20px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  background: 'rgba(0, 212, 255, 0.2)',
                                  color: '#00d4ff',
                                  border: '1px solid rgba(0, 212, 255, 0.4)',
                                }}
                              >
                                Owned: {op.count}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                            <div style={{ background: 'rgba(0, 0, 0, 0.3)', padding: '10px', borderRadius: '8px' }}>
                              <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}>Win Points</div>
                              <div style={{ color: '#00ff88', fontSize: '14px', fontWeight: 600 }}>{op.winPoints ?? '-'}</div>
                            </div>
                            <div style={{ background: 'rgba(0, 0, 0, 0.3)', padding: '10px', borderRadius: '8px' }}>
                              <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}>Lose Points</div>
                              <div style={{ color: '#ff4444', fontSize: '14px', fontWeight: 600 }}>{op.losePoints ?? '-'}</div>
                            </div>
                            <div style={{ background: 'rgba(0, 0, 0, 0.3)', padding: '10px', borderRadius: '8px' }}>
                              <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}>Devices</div>
                              <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>{op.totalDevices}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', color: '#666', fontSize: '11px' }}>
                            <span>Purchased: {new Date(op.firstPurchasedAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState $night={isNightOps}>No operations available</EmptyState>
                  )}
                </SectionContent>
              </SectionCard>
            </SectionStack>
          )}

          {activeTab === 'reviews' && fieldId && (
            <SectionStack $night={isNightOps}>
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>Field Reviews</SectionTitle>
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  {reviewsLoading ? (
                    <LoadingSpinner $night={isNightOps} />
                  ) : (
                    <ReviewsList
                      targetType="field"
                      targetId={fieldId}
                      showSummary={false}
                    />
                  )}
                </SectionContent>
              </SectionCard>
            </SectionStack>
          )}

          {activeTab === 'referees' && fieldId && (
            <SectionStack $night={isNightOps}>
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>
                    {isOwner ? 'Manage Referees' : 'Field Referees'}
                  </SectionTitle>
                  {isOwner && (
                    <ActionButton $night={isNightOps} onClick={() => setIsInviteRefereeModalOpen(true)}>
                      + Invite Referee
                    </ActionButton>
                  )}
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  <RefereeList
                    fieldId={fieldId}
                    isPublicMode={!isOwner}
                    referees={isOwner ? referees : undefined}
                    loading={isOwner ? refereesLoading : undefined}
                    onUpdateStatus={isOwner ? handleUpdateRefereeStatus : undefined}
                    onRemoveReferee={isOwner ? handleRemoveReferee : undefined}
                    onSetMainReferee={isOwner ? handleSetMainReferee : undefined}
                  />
                </SectionContent>
              </SectionCard>
            </SectionStack>
          )}

          {activeTab === 'schedule' && fieldId && (
            <SectionStack $night={isNightOps}>
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>Field Schedule</SectionTitle>
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  <WeeklyCalendar
                    fieldId={fieldId}
                    canEdit={isOwner}
                    onNewsClick={handleOpenNewsDetail}
                    referees={isOwner ? activeReferees : undefined}
                  />
                </SectionContent>
              </SectionCard>
            </SectionStack>
          )}

          {/* Subscription Tab - Owner only */}
          {activeTab === 'subscription' && isOwner && (
            <SectionStack $night={isNightOps}>
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>Subscription Management</SectionTitle>
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  <FieldSubscriptionSection $night={isNightOps} />
                </SectionContent>
              </SectionCard>
            </SectionStack>
          )}

          {/* Edit Info Tab - Owner only */}
          {activeTab === 'edit' && isOwner && fieldId && (
            <SectionStack $night={isNightOps}>
              <SectionCard $night={isNightOps}>
                <SectionHeader $night={isNightOps}>
                  <SectionTitle $night={isNightOps}>Edit Field Information</SectionTitle>
                </SectionHeader>
                <SectionContent $night={isNightOps}>
                  <EditFieldGroup>
                    <EditFieldLabel $night={isNightOps}>Address</EditFieldLabel>
                    <EditInput
                      $night={isNightOps}
                      type="text"
                      value={editingField.address}
                      onChange={(e) => setEditingField(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Enter field address"
                    />
                  </EditFieldGroup>

                  <EditFieldGroup>
                    <EditFieldLabel $night={isNightOps}>Phone</EditFieldLabel>
                    <EditInput
                      $night={isNightOps}
                      type="tel"
                      value={editingField.phone}
                      onChange={(e) => setEditingField(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Enter phone number"
                    />
                  </EditFieldGroup>

                  <EditFieldGroup>
                    <EditFieldLabel $night={isNightOps}>Website</EditFieldLabel>
                    <EditInput
                      $night={isNightOps}
                      type="url"
                      value={editingField.website}
                      onChange={(e) => setEditingField(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="https://example.com"
                    />
                  </EditFieldGroup>

                  <EditFieldGroup>
                    <EditFieldLabel $night={isNightOps}>Description</EditFieldLabel>
                    <EditTextarea
                      $night={isNightOps}
                      value={editingField.description}
                      onChange={(e) => setEditingField(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe your field..."
                    />
                  </EditFieldGroup>

                  <EditFieldGroup>
                    <EditFieldLabel $night={isNightOps}>Supported Game Types</EditFieldLabel>
                    <GameTypesGrid>
                      {['airsoft', 'paintball', 'speedball', 'laser tag'].map(gameType => (
                        <GameTypeCheckbox
                          key={gameType}
                          $night={isNightOps}
                          $checked={editingField.supportedGameTypes.includes(gameType)}
                        >
                          <input
                            type="checkbox"
                            checked={editingField.supportedGameTypes.includes(gameType)}
                            onChange={() => toggleGameType(gameType)}
                          />
                          {gameType.charAt(0).toUpperCase() + gameType.slice(1)}
                        </GameTypeCheckbox>
                      ))}
                    </GameTypesGrid>
                  </EditFieldGroup>

                  <EditButtonsRow>
                    <ActionButton $night={isNightOps} onClick={handleSaveFieldInfo} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </ActionButton>
                    <SecondaryButton $night={isNightOps} onClick={handleResetEditForm}>
                      Reset
                    </SecondaryButton>
                  </EditButtonsRow>
                </SectionContent>
              </SectionCard>
            </SectionStack>
          )}

        </TabContent>

        {/* Modals */}
        <NewsDetailModal
          isOpen={isNewsDetailModalOpen}
          onClose={handleCloseNewsDetail}
          news={selectedNews}
        />

        {/* Create/Edit News Modal - Owner only */}
        {isOwner && (
          <CreateFieldNewsModal
            isOpen={isCreateNewsModalOpen}
            onClose={() => {
              setIsCreateNewsModalOpen(false);
              setEditingNews(null);
            }}
            onSubmit={editingNews ? handleUpdateNews : handleCreateNews}
            editingNews={editingNews}
            upcomingEvents={futureEvents.map(e => ({
              id: e.id,
              title: e.title || 'Untitled Event',
              date: e.date,
              startTime: e.startTime,
              endTime: e.endTime,
            }))}
          />
        )}

        {/* Invite Referee Modal - Owner only */}
        {isOwner && (
          <InviteRefereeModal
            isOpen={isInviteRefereeModalOpen}
            onClose={() => setIsInviteRefereeModalOpen(false)}
            onInvite={handleInviteReferee}
          />
        )}

        {/* Review Modal */}
        <ReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          targetType="field"
          targetId={fieldId || ''}
          targetName={field?.name || ''}
          existingReview={myReview}
          onReviewSubmitted={handleReviewSubmitted}
        />
      </Container>
    </Background>
  );
};

export default UnifiedFieldPage;
